// import { ISchedule, IPouchService, IPouchRows } from "app/app.types";
import {
  IBooking,
  IBookingFromPouch,
  IServiceItem,
  ITableItem,
  servicePaymentType,
  FunctionTrackerInfo,
  IPouchService,
  IPouchRows,
} from 'shared-types/index';
import BookingGeneratorService from '../booking-generator-service';
import moment from 'moment';
import DateUtilsService from '../date-utils-service';
import { cloneDeep, sortBy } from 'lodash-es';
import FunctionTrackerFilterService from 'external-services/function-tracker-service/functionTracker.service';
import {
  IBookingsByServiceObj,
  IBookingService,
} from 'external-services/booking-generator-service/types';
import UtilsService from 'external-services/utils-service/index';
import { IServiceViewItem } from 'external-services/booking-lookup-service/types';
import BookingNormaliseService from 'external-services/booking-normalise-service/bookingNormalise.service';
import { IDiarySchedule, BookingStatusNames, BookingType, ISchedule } from 'shared-types/index';
import PouchLoadService from 'external-services/pouch-load-service/index';
import ObjectCopyService from 'external-services/object-copy-service/index';

const NS = 'BookingLookupService';

class BookingLookupService {
  /**
   * Date is needed so that the correct DB (full or reduced) can be selected.
   */
  public getById(id: string, date: Date): Promise<IBooking> {
    const dateStr: string = DateUtilsService.dateToIdStr(date);
    return PouchLoadService.selectDatabase(dateStr)
      .get(id)
      .then((booking: unknown) => {
        return BookingNormaliseService.normalizeModelToView(booking as IBookingFromPouch);
      });
  }

  /**
   * All customer bookings
   * @param  {[]string} customerIds
   * @param  {boolean} full if you want the full customer history which will require the online database
   */
  public getBookingsByCustomerIds(
    customerIds: (string | number)[],
    full: boolean = false
  ): Promise<IBooking[]> {
    return new Promise((resolve, reject) => {
      // Guarantee ids to be a set of unique values
      if (customerIds.length > 1) {
        customerIds = customerIds.filter((value, index, self) => self.indexOf(value) === index);
      }

      const database: any = full ? PouchLoadService.getDbOnline() : PouchLoadService.getDbLocal();
      const requests: IPouchRows<IBookingFromPouch>[] = [];
      let bookings: IBooking[] = [];

      const chunkSize: number = 100;
      for (let i = 0; i < customerIds.length; i += chunkSize) {
        const chunk: (string | number)[] = customerIds.slice(i, i + chunkSize);
        requests.push(
          database.query('bookings/by_customer_id', {
            keys: chunk,
            include_docs: true,
          })
        );
      }

      Promise.all(requests)
        .then((results: IPouchRows<IBookingFromPouch>[]) => {
          for (const result of results) {
            const a: IBooking[] = result.rows.map((item: { doc: IBookingFromPouch }) => {
              return BookingNormaliseService.normalizeModelToView(item.doc);
            });
            bookings = bookings.concat(a);
          }
          resolve(bookings);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  /**
   * Given a schedule and date will get all the bookings occur in that schedule
   * @param {schedule} [Object]
   * @return {Promise}
   */
  public getBySchedule(schedule: IDiarySchedule): Promise<{
    schedule: IDiarySchedule;
    bookings: IBooking[];
  }> {
    const startDay: Date = schedule.services[0].startTime;
    const nextDay: Date = cloneDeep(startDay);
    nextDay.setDate(nextDay.getDate() + 1);

    const promises: Promise<IBooking[]>[] = [startDay, nextDay].map((day) => this.getByDate(day));
    return Promise.all(promises).then((bookingsArray: IBooking[][]) => {
      let bookings: IBooking[] = [];
      for (const b of bookingsArray) {
        bookings = bookings.concat(b);
      }
      bookings = sortBy(bookings, (booking) => booking.time);

      const dayStartDate: Date = cloneDeep(startDay);
      dayStartDate.setHours(4);
      const dayStartTime: number = dayStartDate.getTime();

      const dayEndDate: Date = cloneDeep(nextDay);
      dayEndDate.setHours(4);
      const dayEndTime: number = dayEndDate.getTime();

      const matchedBookings: IBooking[] = bookings.filter((booking: IBooking) => {
        return booking.time.getTime() >= dayStartTime && booking.time.getTime() < dayEndTime;
      });

      return {
        schedule: schedule,
        bookings: matchedBookings,
      };
    });
  }

  public modifyBookingAndCoversCount(
    bookingsByService: IBookingService,
    newBooking: IBooking,
    bookingIndex: number,
    increment: number
  ) {
    const copied = ObjectCopyService.angularSafeCopy(bookingsByService);
    copied.bookings[bookingIndex] = newBooking;
    copied.bookingsCount += increment;
    if (increment > 0) {
      copied.covers += copied.bookings[bookingIndex].people;
    } else {
      copied.covers -= copied.bookings[bookingIndex].people;
    }
    return copied;
  }
  // ############################################ Private methods ##################################
  /**
   * Returns all bookings for a given date
   * @param {Date} [date]
   * @return {Array} booking[]
   */
  private getByDate(date: Date): Promise<IBooking[]> {
    const dateStr: string = DateUtilsService.dateToIdStr(date);
    const database: any = PouchLoadService.selectDatabase(date);
    return Promise.resolve(
      database
        .allDocs({
          include_docs: true,
          attachments: false,
          startkey: 'booking_' + dateStr,
          endkey: 'booking_' + dateStr + '\uffff',
        })
        .then((bookings: IPouchRows<IBookingFromPouch>) => {
          return bookings.rows.map((item: { doc: IBookingFromPouch }) => {
            const booking = BookingNormaliseService.normalizeModelToView(item.doc);
            return booking;
          });
        })
    );
  }

  public setBookingsByServiceObject(
    serviceViewList: IServiceViewItem[],
    haveAtleastOneFunctionTrackerBooking: boolean
  ) {
    const bookingsByService: IBookingsByServiceObj = {};

    // starts at 1 so that we don't include 'All'
    for (let s = 1; s < serviceViewList.length; s++) {
      const service = serviceViewList[s].service;

      if (service) {
        // only want to include single services, not service groups
        bookingsByService[s] = BookingGeneratorService.makeBookingService(service);
      }
    }

    if (haveAtleastOneFunctionTrackerBooking) {
      // Add function tracker event service at the end of the booking service list inorder to display it on the diary overview area.
      const functionTrackerService = {
        _id: FunctionTrackerInfo.id,
        name: FunctionTrackerInfo.name,
      } as IServiceItem;
      bookingsByService[serviceViewList.length] =
        BookingGeneratorService.makeBookingService(functionTrackerService);
    }
    return bookingsByService;
  }

  public getBookingsByService(
    serviceId: number,
    bookingsByServiceObj: IBookingsByServiceObj,
    serviceViewList: IServiceViewItem[]
  ): IBooking[] {
    /**
     * If `serviceId` is the index of a single service, fetches the booking data from `bookingsByServiceObj`
     */
    const bookingServices: IBookingsByServiceObj = cloneDeep(bookingsByServiceObj);
    const bookingData: IBookingService = bookingServices[serviceId];
    if (bookingData) {
      this.appendFunctionTrackerBookings(bookingData, bookingServices);
      return bookingData.bookings;
    }

    /**
     * If `serviceId` does not exist in `bookingsByServiceObj`, means it is a serviceGroup, so we go through its
     * services and extract the bookings for each service
     */
    const serviceViewData: IServiceViewItem = serviceViewList[serviceId];
    const bookings: IBooking[] = serviceViewData.services?.reduce((acc: any, s: any) => {
      for (const prop in bookingServices) {
        if (bookingServices[prop].service && bookingServices[prop].service._id === s._id) {
          const bookingsObj: IBookingService = bookingServices[prop];
          this.appendFunctionTrackerBookings(bookingsObj, bookingServices);
          acc = acc.concat(bookingsObj.bookings);
        }
      }
      return acc;
    }, []);
    return bookings ?? [];
  }

  public appendFunctionTrackerBookings(
    bookingData: IBookingService,
    bookingServices: IBookingsByServiceObj
  ) {
    // Find function service from the list of services
    const functionTrackerService = Object.values(bookingServices).find(
      (item) => item.service._id === FunctionTrackerInfo.id
    );
    if (!!functionTrackerService) {
      // Extract function tracker bookings

      const ftBookings = FunctionTrackerFilterService.filterBookings(
        functionTrackerService.bookings,
        bookingData.service.sectionLayouts
      );

      // Append the function tracker bookings to the booking list
      bookingData.bookings.push(...ftBookings);
    }
  }

  public filterSlottedBookings(bookings: IBooking[]): IBooking[] {
    return bookings?.filter(
      ({ bookingType, status }) =>
        bookingType === BookingType.Booking ||
        (bookingType === BookingType.StandbyList &&
          status.name === BookingStatusNames.StandbyPending)
    );
  }

  public isTableIntoSelectedSections(selectionSections: string[], tables: ITableItem[]) {
    if (!selectionSections.length) return true;
    const hasMatchedSections = selectionSections.some((section) => {
      const tablesSection = tables.filter((table) => table.sectionId === section);
      return tablesSection && tablesSection.length > 0;
    });

    return hasMatchedSections;
  }

  public filterBookings(
    currentService: number,
    schedule: ISchedule,
    bookings: IBooking[],
    bookingsByService: IBookingsByServiceObj,
    serviceViewList: IServiceViewItem[],
    selectedSectionIds: string[],
    checkMultipleTables: boolean
  ): { filteredBookings: IBooking[]; slottedBookings: IBooking[] } {
    // get the bookings filtered by service if set
    const serviceId = currentService;
    let filteredBookings;
    const bookingsByServiceTemp =
      Number(currentService) === 0 || schedule.services.length === 1
        ? bookings
        : this.getBookingsByService(serviceId, bookingsByService, serviceViewList);

    if (selectedSectionIds) {
      let condition: boolean;
      filteredBookings = bookingsByServiceTemp.filter(({ tables }: IBooking) => {
        if (checkMultipleTables) {
          condition = this.isTableIntoSelectedSections(selectedSectionIds, tables);
        } else if (tables.length > 0) {
          condition = tables[0].sectionId && selectedSectionIds.indexOf(tables[0].sectionId) !== -1;
        }
        return !tables || (tables.length && condition);
      });
    } else {
      filteredBookings = bookingsByServiceTemp;
    }

    // ensures Wait List and Standby List bookings aren't included
    const slottedBookings = this.filterSlottedBookings(filteredBookings || []);
    return { filteredBookings, slottedBookings: cloneDeep(slottedBookings) };
  }

  /**
   * Returns all bookings for a given date
   * @param {Date} [date]
   * @return {Array}
   */
  public getFromDate(date: Date): Promise<IBooking[]> {
    const startDate: Date = cloneDeep(date);
    const dateStr: string = DateUtilsService.dateToIdStr(date);
    /**
     * This function gets used to gather bookings in `webworkers/checkConflicks.js` for a 2 year range.
     * @todo: determine why we need such a large range and if there are performance overheads from doing this check. severity: medium
     */
    const endDateStr: string = DateUtilsService.dateToIdStr(
      date.setFullYear(date.getFullYear() + 2)
    ); // 2 years ahead
    const database: any = PouchLoadService.selectDatabase(startDate); // only use startDate to determine which db gets selected
    return Promise.resolve(
      database
        .allDocs({
          include_docs: true,
          attachments: false,
          startkey: 'booking_' + dateStr,
          endkey: 'booking_' + endDateStr + '\ufff0',
        })
        .then((bookings: IPouchRows<IBookingFromPouch>) => {
          return bookings.rows.map((item: { doc: IBookingFromPouch }) => {
            const booking = BookingNormaliseService.normalizeModelToView(item.doc);
            return booking;
          });
        })
    );
  }

  scriptInjectionPrevention(booking: IBooking) {
    booking.notes = UtilsService.htmlEncode(booking.notes);
    booking.company = UtilsService.htmlEncode(booking.company);
  }

  normalizeModelToView(booking: IBooking) {
    if (booking) {
      try {
        booking.time = DateUtilsService.dateTimeStrToDate(booking.time as any);
      } catch (e) {
        console.log('Fix booking.time', booking._id, booking.time);
        booking.time = moment(booking.time).toDate();
      }

      booking.bookingEnd = new Date(booking.time);
      booking.bookingEnd.setMinutes(booking.bookingEnd.getMinutes() + Number(booking.duration));
      booking.bookedAt = new Date(booking.bookedAt);

      if (
        booking.paymentSummary &&
        booking.paymentSummary.paymentType === servicePaymentType.preAuth
      ) {
        booking.paymentSummary.amountHeld = (
          booking.paymentSummary.amountDue -
          (booking.paymentSummary.preAuthReleasedAmount +
            booking.paymentSummary.preAuthChargedAmount)
        ).toFixed(2);
      }

      booking.primaryTableId = this.getPrimaryTableId(booking);

      booking.tags = booking.tags || [];
      this.scriptInjectionPrevention(booking);
    }
    return booking;
  }

  getPrimaryTableId(booking: IBooking | IBookingFromPouch) {
    let primary = booking.primaryTableId;

    // Guard table(s)
    if (!booking.tables || booking.tables.length === 0) {
      return null;
    }

    // Guard validity of primaryTableId
    if (booking.tables.find((t) => t._id === primary) == null) {
      primary = null;
    }

    // Apply a primaryTableId
    // return primary || $filter('orderBy')(booking.tables, 'name')[0]._id;
    return primary || booking.tables[0]._id;
  }
}

const instance = new BookingLookupService();
export default instance as BookingLookupService;
